/**
 * 🛡️ Unified Route Guards
 * Consolidated route protection system replacing all parallel guard implementations
 * 
 * Features:
 * - Role-based route protection
 * - Permission-based access control
 * - Platform access validation
 * - Consistent redirect handling
 * - Loading state management
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSupabaseAuth } from '@/hooks/useAuthCompatibility';
import { UserRole } from '@/types';
import { Card, CardContent } from '@/components/ui/card';
import { Lock, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingPage } from '@/components/ui/LoadingHeader';
import { SubscriptionCTA, WorkflowSubscriptionCTA } from '@/components/subscription/SubscriptionCTA';

// ============================================================================
// INTERFACES
// ============================================================================

interface RouteGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  redirectTo?: string;
}

interface RoleGuardProps extends RouteGuardProps {
  allowedRoles: UserRole[];
  requireAll?: boolean;
}

interface PermissionGuardProps extends RouteGuardProps {
  requiredPermissions: string[];
  requireAll?: boolean;
}

// ============================================================================
// LOADING COMPONENT
// ============================================================================

const LoadingGuard: React.FC = () => <LoadingPage />;

// ============================================================================
// ACCESS DENIED COMPONENT
// ============================================================================

interface AccessDeniedProps {
  message?: string;
  requiredRole?: string;
  requiredPermission?: string;
  showSubscriptionCTA?: boolean;
  featureType?: 'workflow' | 'agent' | 'analytics' | 'user_management';
}

const AccessDenied: React.FC<AccessDeniedProps> = ({ 
  message,
  requiredRole,
  requiredPermission,
  showSubscriptionCTA = true,
  featureType = 'workflow'
}) => {
  const location = useLocation();
  
  // Determine if this is a feature that should show subscription CTA
  const shouldShowCTA = showSubscriptionCTA && (
    location.pathname.includes('/workflows') ||
    location.pathname.includes('/custom-agents') ||
    location.pathname.includes('/analytics') ||
    location.pathname.includes('/admin')
  );

  if (shouldShowCTA) {
    // Show different CTAs based on the route - but within the page layout
    if (location.pathname.includes('/workflows')) {
      return (
        <div className="flex items-center justify-center min-h-[60vh] p-4">
          <WorkflowSubscriptionCTA className="max-w-md" />
        </div>
      );
    } else if (location.pathname.includes('/custom-agents')) {
      return (
        <div className="flex items-center justify-center min-h-[60vh] p-4">
          <SubscriptionCTA 
            feature="agent" 
            requiredPlan="creator" 
            className="max-w-md" 
          />
        </div>
      );
    } else if (location.pathname.includes('/analytics')) {
      return (
        <div className="flex items-center justify-center min-h-[60vh] p-4">
          <SubscriptionCTA 
            feature="analytics" 
            requiredPlan="pro" 
            className="max-w-md" 
          />
        </div>
      );
    } else if (location.pathname.includes('/admin')) {
      return (
        <div className="flex items-center justify-center min-h-[60vh] p-4">
          <Card className="max-w-md">
            <CardContent className="p-6 text-center">
              <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Admin Access Required</h3>
              <p className="text-muted-foreground mb-4">
                This feature requires admin approval. Contact your system administrator for access.
              </p>
              <Alert>
                <AlertDescription>
                  <strong>Note:</strong> Admin/Seller roles require approval, not payment. Platform fees apply to all users.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  // Fallback to standard access denied message
  return (
    <div className="flex items-center justify-center min-h-[60vh] p-4">
      <Card className="w-96">
        <CardContent className="flex flex-col items-center justify-center p-8">
          <Lock className="w-16 h-16 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
          <Alert className="w-full">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {message || `You don't have permission to access this page.`}
              {requiredRole && (
                <div className="mt-2 text-sm">
                  Required role: <code className="font-mono">{requiredRole}</code>
                </div>
              )}
              {requiredPermission && (
                <div className="mt-2 text-sm">
                  Required permission: <code className="font-mono">{requiredPermission}</code>
                </div>
              )}
              <div className="mt-3 text-xs text-muted-foreground">
                <strong>Note:</strong> Admin/Seller roles require approval, not payment. Platform fees apply to all users.
              </div>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================================
// MAIN ROUTE GUARD
// ============================================================================

export const UnifiedRouteGuard: React.FC<RouteGuardProps> = ({ 
  children, 
  fallback,
  redirectTo = '/login'
}) => {
  const location = useLocation();
  
  // Safely get auth context with error handling
  let authContext;
  try {
    authContext = useSupabaseAuth();
  } catch (error) {
    console.error('UnifiedRouteGuard: Cannot access UnifiedAuthContext - provider not found:', error);
    // Return login redirect if context is not available
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }
  
  const { user, isLoading, isInitialized } = authContext;

  // Show loading while auth is initializing
  if (isLoading || !isInitialized) {
    return fallback || <LoadingGuard />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // User is authenticated, render children
  return <>{children}</>;
};

// ============================================================================
// ROLE-BASED GUARDS
// ============================================================================

export const AdminRoute: React.FC<RouteGuardProps> = ({ children, fallback, redirectTo }) => {
  // Safely get auth context with error handling
  let authContext;
  try {
    authContext = useSupabaseAuth();
  } catch (error) {
    console.error('AdminRoute: Cannot access UnifiedAuthContext - provider not found:', error);
    return <Navigate to={redirectTo || '/login'} replace />;
  }
  
  const { user, hasRole, isLoading, isInitialized } = authContext;

  if (isLoading || !isInitialized) {
    return fallback || <LoadingGuard />;
  }

  if (!user) {
    return <Navigate to={redirectTo || '/login'} replace />;
  }

  if (!hasRole('admin') && !hasRole('super_admin')) {
    return fallback || <AccessDenied requiredRole="admin or super_admin" />;
  }

  return <>{children}</>;
};

export const SuperAdminRoute: React.FC<RouteGuardProps> = ({ children, fallback, redirectTo }) => {
  // Safely get auth context with error handling
  let authContext;
  try {
    authContext = useSupabaseAuth();
  } catch (error) {
    console.error('SuperAdminRoute: Cannot access UnifiedAuthContext - provider not found:', error);
    return <Navigate to={redirectTo || '/login'} replace />;
  }
  
  const { user, hasRole, isLoading, isInitialized } = authContext;

  if (isLoading || !isInitialized) {
    return fallback || <LoadingGuard />;
  }

  if (!user) {
    return <Navigate to={redirectTo || '/login'} replace />;
  }

  if (!hasRole('super_admin')) {
    return fallback || <AccessDenied requiredRole="super_admin" />;
  }

  return <>{children}</>;
};

export const SellerRoute: React.FC<RouteGuardProps> = ({ children, fallback, redirectTo }) => {
  // Safely get auth context with error handling
  let authContext;
  try {
    authContext = useSupabaseAuth();
  } catch (error) {
    console.error('SellerRoute: Cannot access UnifiedAuthContext - provider not found:', error);
    return <Navigate to={redirectTo || '/login'} replace />;
  }
  
  const { user, hasAnyRole, isLoading, isInitialized } = authContext;

  if (isLoading || !isInitialized) {
    return fallback || <LoadingGuard />;
  }

  if (!user) {
    return <Navigate to={redirectTo || '/login'} replace />;
  }

  if (!hasAnyRole(['seller', 'creator_admin', 'admin', 'super_admin'])) {
    return fallback || <AccessDenied requiredRole="seller or higher" />;
  }

  return <>{children}</>;
};

// ============================================================================
// FLEXIBLE ROLE GUARD
// ============================================================================

export const RoleGuard: React.FC<RoleGuardProps> = ({ 
  children, 
  allowedRoles, 
  requireAll = false,
  fallback, 
  redirectTo 
}) => {
  const { user, hasRole, hasAnyRole, isLoading, isInitialized } = useSupabaseAuth();

  if (isLoading || !isInitialized) {
    return fallback || <LoadingGuard />;
  }

  if (!user) {
    return <Navigate to={redirectTo || '/login'} replace />;
  }

  const hasAccess = requireAll 
    ? allowedRoles.every(role => hasRole(role))
    : hasAnyRole(allowedRoles);

  if (!hasAccess) {
    return fallback || (
      <AccessDenied 
        requiredRole={allowedRoles.join(requireAll ? ' and ' : ' or ')} 
      />
    );
  }

  return <>{children}</>;
};

// ============================================================================
// PERMISSION-BASED GUARDS
// ============================================================================

export const PermissionGuard: React.FC<PermissionGuardProps> = ({ 
  children, 
  requiredPermissions, 
  requireAll = true,
  fallback, 
  redirectTo 
}) => {
  const { user, hasPermission, hasAnyPermission, isLoading, isInitialized } = useSupabaseAuth();

  if (isLoading || !isInitialized) {
    return fallback || <LoadingGuard />;
  }

  if (!user) {
    return <Navigate to={redirectTo || '/login'} replace />;
  }

  const hasAccess = requireAll
    ? requiredPermissions.every(permission => hasPermission(permission))
    : hasAnyPermission(requiredPermissions);

  if (!hasAccess) {
    return fallback || (
      <AccessDenied 
        requiredPermission={requiredPermissions.join(requireAll ? ' and ' : ' or ')} 
      />
    );
  }

  return <>{children}</>;
};

// ============================================================================
// UTILITY GUARDS
// ============================================================================

export const AuthenticatedRoute: React.FC<RouteGuardProps> = ({ 
  children, 
  fallback, 
  redirectTo = '/login' 
}) => {
  return (
    <UnifiedRouteGuard fallback={fallback} redirectTo={redirectTo}>
      {children}
    </UnifiedRouteGuard>
  );
};

export const UnauthenticatedRoute: React.FC<RouteGuardProps> = ({ 
  children, 
  fallback, 
  redirectTo = '/dashboard' 
}) => {
  // Safely get auth context with error handling
  let authContext;
  try {
    authContext = useSupabaseAuth();
  } catch (error) {
    console.error('UnauthenticatedRoute: Cannot access UnifiedAuthContext - provider not found:', error);
    // If context is not available, allow access to unauthenticated content
    return <>{children}</>;
  }
  
  const { user, isLoading, isInitialized } = authContext;

  if (isLoading || !isInitialized) {
    return fallback || <LoadingGuard />;
  }

  if (user) {
    // Role-based redirect logic
    let targetPath = redirectTo;
    
    if (redirectTo === '/dashboard') {
      // Use role-based redirection only for default dashboard redirects
      switch (user.role) {
        case 'super_admin':
        case 'admin':
          targetPath = '/dashboard'; // Use unified dashboard for all roles now
          break;
        case 'seller':
        case 'creator_admin':
          targetPath = '/dashboard';
          break;
        default:
          targetPath = '/dashboard';
      }
    }
    
    return <Navigate to={targetPath} replace />;
  }

  return <>{children}</>;
};

export const PublicRoute: React.FC<RouteGuardProps> = ({ 
  children, 
  fallback
}) => {
  const { isLoading, isInitialized } = useSupabaseAuth();

  // Show loading while authentication is initializing
  if (isLoading || !isInitialized) {
    return fallback || <LoadingGuard />;
  }

  // Public routes don't need authentication - just render children
  return <>{children}</>;
};

// ============================================================================
// EXPORTS
// ============================================================================

// Legacy aliases for backward compatibility
export const AdminOnly = AdminRoute;
export const SellerOnly = SellerRoute;

export default UnifiedRouteGuard;
